import { APP_INITIALIZER, ApplicationConfig } from '@angular/core';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { NavigationError, provideRouter, withComponentInputBinding, withInMemoryScrolling, withNavigationErrorHandler } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';

import { routes } from './app.routes';
import { authInterceptor } from '@app/core/interceptors/auth.interceptor';
import { authRefreshInterceptor } from '@app/core/interceptors/auth-refresh.interceptor';
import { AuthStoreService } from '@app/core/services/auth-store.service';
import { environment } from '@env/environment';
import { AnalyticsNavigationService } from '@app/core/analytics/analytics-navigation.service';

const lazyChunkReloadStorageKey = 'action-ledgers.lazy-chunk-reload-at';
const lazyChunkReloadCooldownMs = 60_000;

function initializeApp(authStore: AuthStoreService, analyticsNavigation: AnalyticsNavigationService): () => Promise<void> {
  return async () => {
    await authStore.restoreSession();
    analyticsNavigation.start();
  };
}

function handleNavigationError(error: NavigationError): void {
  if (!isLazyChunkLoadError(error.error)) {
    return;
  }

  const now = Date.now();
  const lastReloadAt = Number(window.sessionStorage.getItem(lazyChunkReloadStorageKey) ?? '0');
  if (Number.isFinite(lastReloadAt) && now - lastReloadAt < lazyChunkReloadCooldownMs) {
    return;
  }

  window.sessionStorage.setItem(lazyChunkReloadStorageKey, String(now));
  window.location.reload();
}

function isLazyChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    message.includes('ChunkLoadError') ||
    message.includes('Loading chunk');
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      routes,
      withComponentInputBinding(),
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'enabled'
      }),
      withNavigationErrorHandler(handleNavigationError)
    ),
    provideHttpClient(withInterceptors([authRefreshInterceptor, authInterceptor])),
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.production,
      registrationStrategy: 'registerWhenStable:30000'
    }),
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: initializeApp,
      deps: [AuthStoreService, AnalyticsNavigationService]
    }
  ]
};
